import { ArrowUpRight, ChevronRight, File } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { fileStats } from '../../../lib/summary-freshness';
import { useNewSince } from '../../../lib/use-last-visited';
import { Badge, Card, Typography } from '../ui';
import { AgentEntryRow } from './AgentEntryRow';
import styles from './FilesSummaryCard.module.css';

// The Files tile — the whole card opens `/files`. `pageName` scopes the click BI to the surface
// it renders on. Two presentations of one card, with their own blurb copy:
//   • Your Agents / a Space's Dashboard (default) — the file glyph 4 from "Files" on one line with the
//     OUTLINED "N New" pill beside it and a 16px drill-in chevron in the primary ink, centred against
//     the whole card (matching the tasks summary beside it — both open an in-app route, so a chevron,
//     not an out-arrow), and ONE line of 12px stats below (Figma `15378:117541`, the 249×72 tile).
//   • Home (`compact`) — the leaner tile (Figma 13528:106926): a size-S Card, NO leading glyph, the
//     title in Display 05 over the shorter "Every file…" blurb, and the out-arrow top-aligned beside it.
//
// **The stat line replaces the two-line blurb, and each context counts the level that informs it.**
// Account-wide the interesting spread is across Spaces ("12 files · 3 spaces"); inside a Space every
// file is in the same Space, so the informative level is the folder ("12 files · 3 folders"). Until the
// read lands — and for an account with nothing filed yet — it falls back to the prose blurb rather than
// rendering "0 files · 0 spaces", which states an emptiness the reader did not ask about.
//
// **The read is one call and never blocks.** The tile paints its title and chevron immediately and the
// stat line + badge fill in when the data arrives; a summary line must never hold up a hub screen.
// `useAgentWorkspaceFiles` is a SINGLE account-wide request (see `pages/api/workspace-files.ts`),
// shared via React Query by every tile on the screen and by the `/files` browser it opens — not a
// fan-out per Space. The `compact` home tile has no stat line, so it does not pay for the read at all.

export function FilesSummaryCard({
  pageName = 'agents',
  compact = false,
  entryRow = false,
  workspaceId,
  workspaceSlug,
}: {
  pageName?: string;
  compact?: boolean;
  /** Mobile Your Agents: render the compact single-row entry tile — title, "N files · M spaces", and an
   *  OUTLINED "N new" pill (Figma 15311:197080) — instead of the desktop glyph-over-stats summary. */
  entryRow?: boolean;
  /** Scopes the stats to that Space. Pair it with `workspaceSlug`; omit both for the account-wide tile. */
  workspaceId?: string;
  /** Opens that Space's own file gallery instead of the cross-workspace one. */
  workspaceSlug?: string;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  // Captured once at mount — see `useNewSince` for why reading the stamp live would make the badge
  // vanish under the reader the moment they came back from `/files`.
  const since = useNewSince('files');
  const filesState = useAgentWorkspaceFiles({ enabled: !compact });

  const open = () => logPageClickBi({ page_name: pageName, button_name: 'open_files' });
  const href = {
    href: workspaceSlug
      ? `${basePath}/workspace/${encodeURIComponent(workspaceSlug)}/files`
      : `${basePath}/files`,
  } as Record<string, string>;

  if (compact) {
    return (
      <Card component="a" interactive size="sm" className={styles.compact} {...href} onClick={open}>
        <div className={styles.compactText}>
          <span className="sym-type-display-05">{t('files.title')}</span>
          <Typography variant="p2" color="secondary">
            {t('files.descriptionHome')}
          </Typography>
        </div>
        <ArrowUpRight size={16} className="dir-flip" aria-hidden />
      </Card>
    );
  }

  // The SAME list the `/files` browser renders (apps dropped, each file joined to its Space), so the
  // tile's count and the screen it opens can never disagree. A Space's tile counts only its own.
  const items =
    filesState.status === 'ready'
      ? filesState.items.filter((file) => !workspaceId || file.workspaceId === workspaceId)
      : [];
  // Before the visit stamp has been read (SSR and the first paint) the mark sits in the future, so
  // nothing counts as new and the badge simply isn't drawn yet.
  const stats = fileStats(items, since ?? Number.POSITIVE_INFINITY);

  // Mobile entry row (Figma 15311:197080): the title, "N files · M spaces", and the OUTLINED "N new"
  // pill (the DS's one outlined Style), then the drill-in chevron. Mirrors native's Files `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={t('files.title')}
        subtitle={
          stats.files === 0
            ? undefined
            : workspaceId
            ? t('files.stats.workspace', { files: stats.files, folders: stats.folders })
            : t('files.stats.account', { files: stats.files, spaces: stats.spaces })
        }
        href={href.href}
        onClick={open}
        testId="agentsFiles"
        trailing={
          stats.newCount > 0 ? (
            <Badge variant="agent-profession" label={t('summary.newBadge', { count: stats.newCount })} />
          ) : undefined
        }
      />
    );
  }

  return (
    <Card component="a" interactive size="sm" className={styles.card} {...href} onClick={open}>
      <div className={styles.body}>
        <span className={styles.title}>
          <File size={16} aria-hidden />
          <Typography variant="h4" color="strong" component="span" className={styles.titleText}>
            {t('files.title')}
          </Typography>
          {stats.newCount > 0 && (
            <Badge variant="agent-profession" className={styles.new} label={t('summary.newBadge', { count: stats.newCount })} />
          )}
        </span>
        {/* One line, ellipsised — the frame's tile is a fixed 72 and the stat line is 16 of it. The
            count is short, but the prose FALLBACK (shown while the read is in flight and for an
            account with nothing filed) is a sentence, and wrapping it grew the whole row to 90.
            Native's entry row clamps its subtitle to one line for the same reason. */}
        <Typography variant="p3" color="secondary" noWrap>
          {stats.files === 0
            ? t('files.description')
            : workspaceId
            ? t('files.stats.workspace', { files: stats.files, folders: stats.folders })
            : t('files.stats.account', { files: stats.files, spaces: stats.spaces })}
        </Typography>
      </div>
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
